import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowRight, Instagram, Facebook, Music2 } from "lucide-react";
import { api } from "../lib/api";
import { LogoMark } from "./Logo";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success(t("footer.nlOk"), { description: t("footer.nlOkDesc") });
      setEmail("");
    } catch {
      toast.error(t("footer.nlErr"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-ligne bg-card2">
      <div className="mx-auto max-w-[1500px] px-6 py-16 lg:px-12 lg:py-24">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <LogoMark size={44} />
              <div>
                <p className="font-serif text-2xl tracking-[0.18em] text-ivoire">B.KOSS</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-or">
                  {t("header.slogan")}
                </p>
              </div>
            </div>
            <p className="mt-8 max-w-sm text-sm leading-relaxed text-argile">
              {t("footer.description")}
            </p>
            <form data-testid="newsletter-form" onSubmit={subscribe} className="mt-8 flex max-w-sm border-b border-ligne pb-2 transition-colors focus-within:border-or">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.emailPh")}
                className="w-full bg-transparent py-2 text-sm text-ivoire placeholder:text-argile/60"
              />
              <button
                data-testid="newsletter-submit-button"
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 pl-4 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-colors hover:text-orclair"
              >
                {t("footer.join")} <ArrowRight size={13} />
              </button>
            </form>
            <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-argile/70">
              {t("footer.circle")}
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow">{t("footer.shopCol")}</p>
            <ul className="mt-6 space-y-3 text-sm text-argile">
              <li><Link data-testid="footer-link-homme" className="transition-colors hover:text-or" to="/boutique?categorie=homme">{t("nav.man")}</Link></li>
              <li><Link data-testid="footer-link-femme" className="transition-colors hover:text-or" to="/boutique?categorie=femme">{t("nav.woman")}</Link></li>
              <li><Link data-testid="footer-link-couple" className="transition-colors hover:text-or" to="/boutique?categorie=couple">{t("footer.catCouple")}</Link></li>
              <li><Link data-testid="footer-link-enfants" className="transition-colors hover:text-or" to="/boutique?categorie=enfants">{t("nav.kids")}</Link></li>
              <li><Link data-testid="footer-link-maroquinerie" className="transition-colors hover:text-or" to="/boutique?categorie=maroquinerie">{t("footer.catLeather")}</Link></li>
              <li><Link data-testid="footer-link-boutique" className="transition-colors hover:text-or" to="/boutique">{t("footer.allPieces")}</Link></li>
              <li><Link data-testid="footer-link-presse" className="transition-colors hover:text-or" to="/presse">{t("footer.presse")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="eyebrow">{t("footer.maisonCol")}</p>
            <ul className="mt-6 space-y-3 text-sm text-argile">
              <li>{t("footer.addr1")}</li>
              <li>{t("footer.addr2")}</li>
              <li>
                <a data-testid="footer-phone-1" href="tel:+2250757365385" className="transition-colors hover:text-or">+225 07 57 36 53 85</a>
              </li>
            </ul>
            <div className="mt-6 flex gap-4">
              <a data-testid="footer-social-instagram" href="https://www.instagram.com/b.kossofficial/" target="_blank" rel="noreferrer" className="flex h-10 w-10 items-center justify-center border border-ligne text-argile transition-all hover:border-or hover:text-or" aria-label="Instagram">
                <Instagram size={16} />
              </a>
              <a data-testid="footer-social-facebook" href="https://web.facebook.com/www.bkoss.net" target="_blank" rel="noreferrer" className="flex h-10 w-10 items-center justify-center border border-ligne text-argile transition-all hover:border-or hover:text-or" aria-label="Facebook">
                <Facebook size={16} />
              </a>
              <a data-testid="footer-social-tiktok" href="https://www.tiktok.com/@b.kossofficial" target="_blank" rel="noreferrer" className="flex h-10 w-10 items-center justify-center border border-ligne text-argile transition-all hover:border-or hover:text-or" aria-label="TikTok">
                <Music2 size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-ligne pt-8 sm:flex-row sm:items-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-argile/70">
            © {new Date().getFullYear()} B.KOSS — {t("footer.rights")}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-argile/70">
            Abidjan · New York · Milan
          </p>
        </div>
      </div>
    </footer>
  );
};
